import React, { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send, AlertTriangle, Phone, ArrowRight, Loader2, MessageCircle } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { toast } from "sonner";

const STORAGE_KEY = "noi_symptom_session";

// Hide the launcher on authed dashboards / report viewer / case detail pages
// AND on auth pages (login/signup) — it's purely a top-of-funnel CTA for cold
// traffic on marketing/public pages.
const HIDDEN_PATH_PREFIXES = [
  "/patient", "/doctor", "/admin",
  "/case/", "/report/", "/cases/",
  "/login", "/signup", "/forgot-password", "/reset-password",
];

const PRIORITY_STYLE = {
  urgent:   { label: "Urgent · 12h",   className: "bg-rose-50 border-rose-200 text-rose-700" },
  standard: { label: "Standard · 48h", className: "bg-blue-50 border-blue-200 text-blue-700" },
  routine:  { label: "Routine",        className: "bg-slate-50 border-slate-200 text-slate-700" },
};

function ChatMessage({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-2`}>
      <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${
        isUser
          ? "bg-blue-700 text-white rounded-br-sm"
          : "bg-slate-100 text-slate-900 rounded-bl-sm"
      }`}>
        {content}
      </div>
    </div>
  );
}

function RecommendationCard({ rec, sessionId }) {
  if (!rec) return null;
  const ps = PRIORITY_STYLE[rec.suggested_priority] || PRIORITY_STYLE.standard;
  const isEmergency = rec.suggested_priority === "urgent" && (rec.red_flags?.length || 0) > 0;
  const slug = rec.suggested_condition_slug && rec.suggested_condition_slug !== "general"
    ? rec.suggested_condition_slug : null;

  // Build a deeplink that pre-fills the booking wizard.
  // We pass: priority (urgent|standard) · slug for tag mapping · symptoms summary · sym_session for analytics.
  const wizardPriority = rec.suggested_priority === "routine" ? "standard" : rec.suggested_priority;
  const params = new URLSearchParams();
  if (slug) params.set("slug", slug);
  if (wizardPriority) params.set("priority", wizardPriority);
  if (rec.summary) params.set("symptoms", rec.summary.slice(0, 280));
  if (sessionId) params.set("sym_session", sessionId);
  const bookHref = `/login?as=patient&next=${encodeURIComponent(`/patient?${params.toString()}`)}`;

  return (
    <div className="my-2 rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="symptom-recommendation">
      <div className={`px-3 py-2 border-b ${isEmergency ? "bg-rose-50 border-rose-200" : "bg-emerald-50 border-emerald-200"}`}>
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] flex items-center gap-1.5">
          {isEmergency
            ? <><AlertTriangle className="w-3 h-3 text-rose-600"/> <span className="text-rose-700">Possible emergency</span></>
            : <><Sparkles className="w-3 h-3 text-emerald-600"/> <span className="text-emerald-700">Recommendation</span></>}
        </div>
        <div className="font-heading text-sm font-semibold text-slate-900 mt-1 leading-snug">
          {rec.summary}
        </div>
      </div>
      <div className="px-3 py-3 space-y-2 text-xs">
        <div className="flex flex-wrap gap-1.5">
          <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${ps.className}`}>
            {ps.label}
          </span>
          {rec.suggested_specialty && (
            <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-700">
              {rec.suggested_specialty}
            </span>
          )}
        </div>
        {rec.red_flags?.length > 0 && (
          <ul className="text-[11px] text-rose-800 list-disc list-inside space-y-0.5 leading-snug">
            {rec.red_flags.slice(0, 4).map((f, i) => <li key={i}>{f}</li>)}
          </ul>
        )}
        {rec.next_action && (
          <p className="text-[11px] text-slate-700 italic leading-snug">{rec.next_action}</p>
        )}

        <div className="flex flex-col sm:flex-row gap-2 pt-1">
          {isEmergency ? (
            <a href="tel:108" className="flex-1">
              <Button className="w-full h-9 bg-rose-600 hover:bg-rose-700 text-white" data-testid="symptom-call-108-btn">
                <Phone className="w-3.5 h-3.5 mr-1"/> Call 108 now
              </Button>
            </a>
          ) : (
            <Link to={bookHref} className="flex-1">
              <Button className="w-full h-9 bg-blue-700 hover:bg-blue-800 text-white" data-testid="symptom-book-btn">
                Book second opinion <ArrowRight className="w-3.5 h-3.5 ml-1"/>
              </Button>
            </Link>
          )}
          {slug && (
            <Link to={`/conditions/${slug}`} className="flex-1">
              <Button variant="outline" className="w-full h-9 border-slate-300" data-testid="symptom-learn-btn">
                Learn more
              </Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SymptomChecker() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);  // [{role, content, recommendation?}]
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const scrollRef = useRef(null);

  // Hide on authed / case-specific routes — checked AFTER all hooks to keep order stable.
  const hidden = HIDDEN_PATH_PREFIXES.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    try { setSessionId(localStorage.getItem(STORAGE_KEY) || null); }
    catch (e) { /* ignore */ }
  }, []);

  // Auto-scroll on new message
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, busy]);

  // Greeting on first open
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        role: "assistant",
        content: "Hi, I'm NeuroBot 👋  I can help you figure out the right specialist + urgency for your case. What symptom or scan finding is bothering you? You can write in English, Hindi or Telugu.",
      }]);
      // Reflective prompts to get patients started
      setFollowUps([
        "Severe headache for 3 days",
        "MRI shows brain tumor",
        "Slipped disc, leg pain",
        "Father has memory loss",
      ]);
      // GA4 tracking
      if (typeof window !== "undefined" && window.gtag) {
        window.gtag("event", "symptom_checker_open");
      }
    }
  }, [open]);  // eslint-disable-line react-hooks/exhaustive-deps

  const sendMessage = async (text) => {
    const t = (text || input).trim();
    if (!t || busy) return;
    setMessages((m) => [...m, { role: "user", content: t }]);
    setInput("");
    setFollowUps([]);
    setBusy(true);
    try {
      const r = await api.post("/ai/symptom-check", { message: t, session_id: sessionId });
      if (!sessionId && r.data.session_id) {
        setSessionId(r.data.session_id);
        try { localStorage.setItem(STORAGE_KEY, r.data.session_id); }
        catch (e) { /* ignore */ }
      }
      setMessages((m) => [...m, {
        role: "assistant",
        content: r.data.reply,
        recommendation: r.data.ready_to_recommend ? r.data.recommendation : null,
      }]);
      setFollowUps(r.data.follow_up_questions || []);
      if (r.data.ready_to_recommend && typeof window !== "undefined" && window.gtag) {
        window.gtag("event", "symptom_checker_recommended", {
          priority: r.data.recommendation?.suggested_priority || "unknown",
          condition: r.data.recommendation?.suggested_condition_slug || "unknown",
        });
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "Sorry, I couldn't process that. Please try again or WhatsApp our care team.";
      toast.error(msg);
      setMessages((m) => [...m, { role: "assistant", content: msg }]);
    } finally { setBusy(false); }
  };

  const reset = () => {
    setMessages([]);
    setSessionId(null);
    setFollowUps([]);
    try { localStorage.removeItem(STORAGE_KEY); }
    catch (e) { /* ignore */ }
    setOpen(false);
  };

  return (
    <>
      {/* Floating launcher button — bottom-right, above WhatsApp FAB */}
      {!open && !hidden && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-4 z-40 group flex items-center gap-2 px-4 h-12 rounded-full bg-gradient-to-r from-blue-700 to-teal-600 text-white shadow-xl shadow-blue-500/30 hover:shadow-2xl hover:scale-105 transition-all"
          aria-label="Open AI symptom checker"
          data-testid="symptom-checker-fab"
        >
          <Sparkles className="w-4 h-4 animate-pulse"/>
          <span className="text-sm font-semibold hidden sm:inline">Ask NeuroBot</span>
          <span className="text-sm font-semibold sm:hidden">AI</span>
        </button>
      )}

      {/* Slide-up chat panel */}
      {open && !hidden && (
        <div className="fixed inset-x-0 bottom-0 z-50 sm:inset-auto sm:right-4 sm:bottom-4 sm:w-[420px] flex justify-center sm:justify-end pointer-events-none">
          <div className="w-full sm:w-[420px] h-[85vh] sm:h-[640px] max-h-[85vh] bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden pointer-events-auto animate-in slide-in-from-bottom-8 fade-in-25 duration-300"
               data-testid="symptom-checker-panel">
            {/* Header */}
            <div className="px-4 py-3 bg-gradient-to-r from-blue-700 to-teal-600 text-white flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-white/20 grid place-items-center">
                <Sparkles className="w-5 h-5"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold text-sm leading-tight">NeuroBot</div>
                <div className="text-[11px] text-white/80 leading-tight">AI symptom triage · not a diagnosis</div>
              </div>
              <button onClick={reset} className="text-[10px] uppercase tracking-wider text-white/70 hover:text-white" data-testid="symptom-reset-btn">Reset</button>
              <button onClick={() => setOpen(false)} className="p-1 rounded-full hover:bg-white/15" aria-label="Close" data-testid="symptom-close-btn">
                <X className="w-5 h-5"/>
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 bg-gradient-to-b from-slate-50 to-white" data-testid="symptom-messages">
              {messages.map((m, i) => (
                <React.Fragment key={i}>
                  <ChatMessage role={m.role} content={m.content}/>
                  {m.recommendation && <RecommendationCard rec={m.recommendation} sessionId={sessionId}/>}
                </React.Fragment>
              ))}
              {busy && (
                <div className="flex items-center gap-2 px-3 py-2 text-xs text-slate-500">
                  <Loader2 className="w-3.5 h-3.5 animate-spin"/> NeuroBot is thinking…
                </div>
              )}
            </div>

            {/* Suggested follow-ups */}
            {followUps.length > 0 && !busy && (
              <div className="px-3 py-2 border-t border-slate-100 bg-white">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Quick replies</div>
                <div className="flex flex-wrap gap-1.5">
                  {followUps.map((q, i) => (
                    <button key={i} onClick={() => sendMessage(q)}
                            className="text-xs px-2.5 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-800 hover:bg-blue-100 transition"
                            data-testid={`symptom-quickreply-${i}`}>
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
                  className="px-3 py-2.5 border-t border-slate-200 bg-white flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Describe your symptoms…"
                className="flex-1 bg-slate-100 rounded-full px-3.5 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-300"
                disabled={busy}
                data-testid="symptom-input"
              />
              <button type="submit" disabled={busy || !input.trim()}
                      className="w-10 h-10 rounded-full bg-blue-700 text-white grid place-items-center disabled:opacity-50 hover:bg-blue-800 transition"
                      data-testid="symptom-send-btn"
                      aria-label="Send">
                <Send className="w-4 h-4"/>
              </button>
            </form>

            {/* Disclaimer footer */}
            <div className="px-3 pb-2 text-[10px] text-slate-400 text-center leading-tight bg-white">
              NeuroBot guides triage only. For an actual medical opinion, book a verified specialist.{" "}
              <a href="https://wa.me/918317674160" className="text-emerald-600 font-semibold inline-flex items-center gap-0.5">
                <MessageCircle className="w-3 h-3"/> WhatsApp us
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
